import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { ComparisonSeries, fmtMinutes } from '../../hooks/event-comparison'
import SalesCurveComparison, { CurveSeries } from './SalesCurveComparison'
import IndicatorCard from '../DashboardCharts/IndicatorCard'
import { SERIES_BASE, SERIES_COMPARE } from './styles'

const INK = '#212121'
const FAINT = '#B0B0B0'

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 24px 0 12px;
`
const Kicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const Name = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const Note = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  color: ${FAINT};
`
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`
const FullRow = styled.div`
  margin-bottom: 24px;
`

/* velocidade de vendas */
const VRow = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: center;
  gap: 12px;
  padding: 9px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const VLabel = styled.span`
  font-size: 12.5px;
  font-weight: 660;
  color: ${INK};
`
const VBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`
const VTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const VFill = styled.div<{ $w: number; $c: string }>`
  height: 9px;
  border-radius: 5px;
  min-width: 3px;
  background: ${(p) => p.$c};
  width: ${(p) => Math.max(p.$w, 0)}%;
`
const VPct = styled.span<{ $c: string }>`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$c};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const VHint = styled.div`
  font-size: 11px;
  color: ${FAINT};
  margin-top: 10px;
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`

const cumulative = (arr: number[]) => {
  let s = 0
  return arr.map((v) => (s += v))
}
/** R$ compacto para eixo/tooltip: R$ 1,2 mi · R$ 320 mil · R$ 86. */
const fmtBRLc = (n: number): string => {
  if (Math.abs(n) >= 1_000_000) return 'R$ ' + (n / 1_000_000).toFixed(1).replace('.', ',') + ' mi'
  if (Math.abs(n) >= 1_000) return 'R$ ' + Math.round(n / 1_000) + ' mil'
  return 'R$ ' + Math.round(n)
}

interface Props {
  baseSeries?: ComparisonSeries
  compareSeries?: ComparisonSeries
  excludeCourtesy: boolean
  /** janela por minutos (OP-3517): `dias` são baldes deste tamanho; rótulos viram +Nmin. */
  bucketMinutes?: number
}

/** Seção RITMO — velocidade e evolução das vendas por ciclo. OP-3415. Sem backend novo. */
const RitmoSection: FC<Props> = ({ baseSeries, compareSeries, excludeCourtesy, bucketMinutes }) => {
  const qty = (e: ComparisonSeries) =>
    e.dias.map((d) => (excludeCourtesy ? d.ingressosPagos : d.ingressos))

  const minuteMode = bucketMinutes != null
  // rótulos por balde: início (eixo dos gráficos) e fim (marco atingido na velocidade)
  const bucketStart = (i: number) => (i === 0 ? '0' : `+${fmtMinutes(i * (bucketMinutes as number))}`)
  const markLabel = (i: number) =>
    minuteMode ? `até ${fmtMinutes((i + 1) * (bucketMinutes as number))}` : `D+${i}`
  const maxSeriesLen = Math.max(baseSeries?.dias.length || 0, compareSeries?.dias.length || 0)
  const chartLabels = minuteMode
    ? Array.from({ length: maxSeriesLen }, (_, i) => bucketStart(i))
    : undefined
  const chartXTitle = minuteMode ? 'Tempo desde a 1ª venda (janela exata)' : undefined

  // receita acumulada por dia de ciclo
  const receitaSeries: CurveSeries[] = useMemo(() => {
    const out: CurveSeries[] = []
    if (baseSeries)
      out.push({ nome: baseSeries.nome || 'Base', cor: SERIES_BASE, pontos: cumulative(baseSeries.dias.map((d) => d.receita)) })
    if (compareSeries)
      out.push({ nome: compareSeries.nome || 'Comparado', cor: SERIES_COMPARE, pontos: cumulative(compareSeries.dias.map((d) => d.receita)) })
    return out
  }, [baseSeries, compareSeries])

  // ticket médio por dia (receita/ingressos pagos do dia); dia sem venda paga = gap
  const ticketSeries: CurveSeries[] = useMemo(() => {
    const build = (e: ComparisonSeries): (number | null)[] =>
      e.dias.map((d) => (d.ingressosPagos > 0 ? d.receita / d.ingressosPagos : null))
    const out: CurveSeries[] = []
    if (baseSeries) out.push({ nome: baseSeries.nome || 'Base', cor: SERIES_BASE, pontos: build(baseSeries) })
    if (compareSeries) out.push({ nome: compareSeries.nome || 'Comparado', cor: SERIES_COMPARE, pontos: build(compareSeries) })
    return out
  }, [baseSeries, compareSeries])

  // velocidade: dia (D+n) em que cada evento atinge 25/50/75/100% do total
  const velocidade = useMemo(() => {
    const MS = [25, 50, 75, 100]
    const dayFor = (e: ComparisonSeries | undefined, pct: number): number | null => {
      if (!e) return null
      const cum = cumulative(qty(e))
      const total = cum[cum.length - 1] || 0
      if (total <= 0) return null
      const target = (pct / 100) * total
      const idx = cum.findIndex((v) => v >= target)
      return idx < 0 ? null : idx
    }
    const rows = MS.map((pct) => ({ pct, a: dayFor(baseSeries, pct), b: dayFor(compareSeries, pct) }))
    const maxDay = Math.max(1, ...rows.flatMap((r) => [r.a ?? 0, r.b ?? 0]))
    return { rows, maxDay }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries, compareSeries, excludeCourtesy])

  const hasVel = velocidade.rows.some((r) => r.a != null || r.b != null)

  return (
    <>
      <SectionHead>
        <Kicker>Ritmo</Kicker>
        <Name>Velocidade e evolução das vendas</Name>
        <Note>
          {minuteMode
            ? 'Séries por minutos desde a 1ª venda · janela exata'
            : 'Séries por dia de ciclo (D+n) · na janela do período'}
        </Note>
      </SectionHead>

      <FullRow>
        <SalesCurveComparison
          series={receitaSeries}
          mode="cum"
          title="Curva de receita acumulada"
          yFormat={fmtBRLc}
          emptyLabel="Selecione dois eventos para comparar a receita."
          labels={chartLabels}
          xTitle={chartXTitle}
        />
      </FullRow>

      <Grid>
        <IndicatorCard title="Velocidade de vendas">
          {!hasVel ? (
            <Empty>Sem vendas suficientes para medir a velocidade.</Empty>
          ) : (
            <>
              {velocidade.rows.map((r) => (
                <VRow key={r.pct}>
                  <VLabel>{r.pct}%</VLabel>
                  <VBars>
                    <VTrack>
                      <VFill $w={r.a != null ? (r.a / velocidade.maxDay) * 100 : 0} $c={SERIES_BASE} />
                      <VPct $c={SERIES_BASE}>{r.a != null ? markLabel(r.a) : '—'}</VPct>
                    </VTrack>
                    <VTrack>
                      <VFill $w={r.b != null ? (r.b / velocidade.maxDay) * 100 : 0} $c={SERIES_COMPARE} />
                      <VPct $c={SERIES_COMPARE}>{r.b != null ? markLabel(r.b) : '—'}</VPct>
                    </VTrack>
                  </VBars>
                </VRow>
              ))}
              <VHint>
                {minuteMode
                  ? 'Tempo desde a 1ª venda em que cada evento atingiu o % do total vendido na janela — menor = vendeu mais rápido.'
                  : 'Dia de ciclo em que cada evento atingiu o % do seu total vendido — menor = vendeu mais rápido.'}
              </VHint>
            </>
          )}
        </IndicatorCard>

        <SalesCurveComparison
          series={ticketSeries}
          mode="daily"
          title={minuteMode ? 'Ticket médio por intervalo' : 'Ticket médio por dia de ciclo'}
          yFormat={fmtBRLc}
          emptyLabel="Selecione dois eventos para comparar o ticket."
          labels={chartLabels}
          xTitle={chartXTitle}
        />
      </Grid>
    </>
  )
}

export default RitmoSection
